import { createExternalResourceSelectors } from '@next/arable-lib/external-resource';
import { createSelector } from '@ngrx/store';

import { selectCropYear } from '../../store/selectors';
import { tgaFeature } from './tga.feature';
import { NotificationDto, NotificationType, StepStatus } from '../shared/models/models';

export const selectTgaState = tgaFeature.selectTgaState;

export const selectGeneralQuestions = createSelector(selectTgaState, (state) => state.questionnaire.specificQuestionnaire.generalQuestions);

export const selectCropFields = createSelector(selectTgaState, (state) => state.cropFields);
export const selectSoilQuestions = createSelector(selectTgaState, (state) => state.questionnaire.specificQuestionnaire.soilQuestions);
export const selectPlantingQuestions = createSelector(selectTgaState, (state) => state.questionnaire.specificQuestionnaire.plantingQuestions);

export const selectGenericGeneralQuestions = createSelector(selectTgaState, (state) => state.questionnaire.genericQuestionnaire.generalQuestions);
export const selectGenericSoilQuestions = createSelector(selectTgaState, (state) => state.questionnaire.genericQuestionnaire.soilQuestions);
export const selectGenericPlantingQuestions = createSelector(selectTgaState, (state) => state.questionnaire.genericQuestionnaire.plantingQuestions);

export const selectCropFieldById = (cropFieldId: number | null) =>
  createSelector(selectTgaState, (state) => {
    return state?.cropFields?.find((field) => field.cropField.id === Number(cropFieldId)) || null;
  });

// Advice selectors
export const selectCropYearAndCropIdForGeneralAdvice = createSelector(selectTgaState, (state) => {
  return {
    cropFieldId: state.advice.generalAdvice?.cropFieldId,
    cropYear: state.advice.generalAdvice?.cropYear,
  };
});
export const selectCropYearAndCropIdForSoilAdvice = createSelector(selectTgaState, (state) => {
  return {
    cropFieldId: state.advice.soilAdvice?.cropFieldId,
    cropYear: state.advice.soilAdvice?.cropYear,
  };
});
export const selectCropYearAndCropIdForPlantingAdvice = createSelector(selectTgaState, (state) => {
  return {
    cropFieldId: state.advice.plantingAdvice?.cropFieldId,
    cropYear: state.advice.plantingAdvice?.cropYear,
  };
});
export const selectCropYearAndCropIdForCultivationAdvice = createSelector(selectTgaState, (state) => {
  return {
    cropFieldId: state.advice.cultivationAdvice?.cropFieldId,
    cropYear: state.advice.cultivationAdvice?.cropYear,
  };
});

export const selectGeneralAdvice = createSelector(selectTgaState, (state) => state.advice.generalAdvice);
export const selectSoilAdvice = createSelector(selectTgaState, (state) => state.advice.soilAdvice);
export const selectPlantingAdvice = createSelector(selectTgaState, (state) => state.advice.plantingAdvice);
export const selectCultivationAdvice = createSelector(selectTgaState, (state) => state.advice.cultivationAdvice);


export const selectSoilAdviceTranslationParams = createSelector(
  selectSoilAdvice,
  (soilAdvice) => soilAdvice?.translationParams || {},
);

export const selectPlantingAdviceTranslationParams = createSelector(
  selectPlantingAdvice,
  (plantingAdvice) => plantingAdvice?.translationParams || {},
);
export const selectCultivationAdviceTranslationParams = createSelector(
  selectCultivationAdvice,
  (cultivationAdvice) => cultivationAdvice?.translationParams || {},
);

// Stepper selectors
export const selectStepperState = createSelector(selectTgaState, (state) => state.stepper);
export const selectActiveStepName = createSelector(selectStepperState, (state) => state.activeStep);
export const selectActiveStep = createSelector(selectActiveStepName, (activeStepName) => {
  return {
    translationKey: activeStepName,
    status: StepStatus.Active,
  };
});

export const TgaSoilImageSelectors = createExternalResourceSelectors(
  tgaFeature.selectTgaState,
  (state) => state.soilImageData,
);

export const TgaBiomassImageSelectors = createExternalResourceSelectors(
  tgaFeature.selectTgaState,
  (state) => state.biomassImageData,
);

export const TgaBiomassPotatoImageSelectors = createExternalResourceSelectors(
  tgaFeature.selectTgaState,
  (state) => state.biomassPotatoImageData,
);

export const getTgaCropYear = createSelector(selectCropYear, (cropYear) => cropYear);

export const selectLastGenericStepCompleted = createSelector(
  selectTgaState,
  (state) => state.questionnaire.lastGenericStepCompleted,
);

export const selectLastSpecificStepCompleted = createSelector(
  selectTgaState,
  (state) => {
    const cropFieldId = state.questionnaire.specificQuestionnaire.cropFieldId;
    if (!cropFieldId) return null;

    const cropField = state.cropFields?.find((field) => field.cropField.id === cropFieldId);
    return cropField?.questionnaireStatus.lastSpecificStepCompleted || null;
  },
);

export const TgaHeightImageSelectors = createExternalResourceSelectors(
  tgaFeature.selectTgaState,
  (state) => state.heightImageData,
);

export const TgaSatelliteImageSelectors = createExternalResourceSelectors(
  tgaFeature.selectTgaState,
  (state) => state.satelliteImages,
);

export const TgaFieldObservationImageSelectors = createExternalResourceSelectors(
  tgaFeature.selectTgaState,
  (state) => state.fieldObservationImages,
);

export const selectWaterAuthorityExceedances = createSelector(
  selectTgaState,
  (state) => state.waterAuthority.exceedances,
);

export const selectLoadingWaterAuthorityExceedances = createSelector(
  selectTgaState,
  (state) => state.waterAuthority.loading,
);
export const selectNotifications = createSelector(selectTgaState, (state) => state.notifications);

export const selectDismissedNotifications = createSelector(
  selectTgaState,
  (state) => state.dismissedNotifications,
);

export const selectAllNotifications = createSelector(
  selectNotifications,
  selectDismissedNotifications,
  (notifications, dismissed) => {
    const currentNotifications = notifications.state === 'loaded' ? notifications.data : [];
    return [...currentNotifications, ...dismissed].sort((a, b) => {
      const dateA = 'createdAt' in a ? (a.createdAt as Date) : new Date();
      const dateB = 'createdAt' in b ? (b.createdAt as Date) : new Date();
      return dateB.getTime() - dateA.getTime();
    });
  },
);

const NOTIFICATION_SUBJECT_PRIORITY: Partial<Record<NotificationType, number>> = {
  [NotificationType.BemReportAvailable]: 1,
  [NotificationType.FieldObservationAdded]: 2,
  [NotificationType.SatelliteImagesAvailable]: 3,
};

function isSatelliteNotification(notification: NotificationDto): boolean {
  return notification.type === NotificationType.SatelliteImagesAvailable;
}

function getNotificationTimestamp(notification: NotificationDto): number {
  if (!notification.createdAt) {
    return 0;
  }
  return new Date(notification.createdAt).getTime();
}

export const selectSortedNotifications = createSelector(selectNotifications, (notificationsResource) => {
  const notifications = notificationsResource.state === 'loaded' ? notificationsResource.data : [];
  return [...notifications].sort((a, b) => {
    if (isSatelliteNotification(a) !== isSatelliteNotification(b)) {
      return isSatelliteNotification(a) ? 1 : -1;
    }

    const dateA = getNotificationTimestamp(a);
    const dateB = getNotificationTimestamp(b);
    if (dateB !== dateA) {
      return dateB - dateA;
    }
    const priorityA = NOTIFICATION_SUBJECT_PRIORITY[a.type] ?? 0;
    const priorityB = NOTIFICATION_SUBJECT_PRIORITY[b.type] ?? 0;
    return priorityA - priorityB;
  });
});
